import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const Banner = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(to right, #1e40af, #ef233c)',
  color: theme.palette.common.white,
  // Fixed header height to match other pages (Owner & Explore)
  height: theme.spacing(17),
  // Horizontal padding responsive to match AppBar, plus extra right padding for actions (px: xs=1+6, md=4+6)
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(7),
  [theme.breakpoints.up('md')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(10),
  },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  borderBottomLeftRadius : theme.shape.borderRadius * 2,
  borderBottomRightRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
  position: 'relative',
  zIndex: 0,  // establish stacking context so negative z-index waveform shows above background
  overflow: 'hidden',
}));

/**
 * PageHeader renders a gradient banner with title, optional subtitle, and optional flair image.
 */
export interface TitleHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  flairImage?: string;
  /** Optional action node positioned bottom-right */
  actions?: React.ReactNode;
  /** If true, renders a background waveform animation */
  waveform?: boolean;
}
export default function TitleHeader({
  title,
  subtitle,
  actions,
  waveform = false,
}: TitleHeaderProps) {
  return (
    <Banner>
      {waveform && (
        <Box
          component="svg"
          viewBox="0 0 144 60"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
          }}
        >
          {/* Static waveform lines */}
          {/* Primary waveform */}
          <path
            stroke="white"
            strokeWidth="2"
            // reduced transparency by 30% => increased opacity
            strokeOpacity="0.05"
            fill="none"
            d="M0 30 Q10 10,20 30 T40 30 T60 30 T80 30 T100 30 T120 30 T140 30"
          />
          {/* Secondary waveform */}
          <path
            stroke="white"
            strokeWidth="1.5"
            strokeOpacity="0.03"
            fill="none"
            d="M0 30 Q15 15,30 30 T60 30 T90 30 T120 30 T150 30"
          />
          {/* Tertiary waveform */}
          <path
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.01"
            fill="none"
            d="M0 30 Q5 20,10 30 T20 30 T30 30 T40 30 T50 30 T60 30 T70 30"
          />
        </Box>
      )}
      <Typography
        variant="h3"
        component="h1"
        sx={(theme) => ({
            pl: 1,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            // Decrease size on smaller viewports: large (>=md), medium (<md), small (<sm)
            fontSize: theme.typography.h3.fontSize,
            [theme.breakpoints.down('md')]: {
                fontSize: '2.50rem',
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: '2.25rem',
            },
        })}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="subtitle1" sx={{
            pl: 1,
            fontFamily: 'Inter, sans-serif',
            opacity: 0.9}}>
          {subtitle}
        </Typography>
      )}
      {actions && (
        <Box sx={(theme) => ({
          position: 'absolute',
          bottom: theme.spacing(1),
          right: theme.spacing(1),
        })}>
          {actions}
        </Box>
      )}
    </Banner>
  );
}